import { LoadStatus } from '../load-status';

/**
 * Feature key under which the app-config slice is registered in the root store.
 */
export const APP_CONFIG_FEATURE_KEY = 'appConfig';

/** Naked, camelCase counterpart of {@link LocationTrackerAdminConfigResponse} (the wire type). */
export type LocationTrackerAdminConfigState = Readonly<{
    enabled: boolean;
    privacyZoneRadiusMeters: number;
    precisionMeters: number;
}>;

/**
 * The persisted projection — the config values themselves, without the load lifecycle.
 *
 * `locationTracker` stays `| undefined`, mirroring the legacy `AppConfigService.locationTracker$`
 * (`LocationTrackerConfig | undefined`, derived from `config?.LocationTracker`) and
 * `isLocationTrackerEnabled$` (`?.Enabled ?? false`). A backend response that omits the
 * `LocationTracker` module — or a slice that has never loaded — must keep behaving the same way,
 * so this type does not fall back to a default entry the way `locationTracker`'s own slice does.
 */
export type AppConfigValuesState = Readonly<{
    locationTracker: LocationTrackerAdminConfigState | undefined;
}>;

/**
 * `status` tracks the BACKEND FETCH ONLY, unlike the `config`/`value-help` slices where a cache
 * hydration also flips it to `Loaded`. Here that would let boot succeed offline purely from
 * cache, where today `AppConfigService.init()` fails with an `InitError` when the network call
 * fails. Keeping `status` backend-only preserves today's boot behaviour while still giving the
 * location-tracker page cached values offline — the same split `LocationTrackerState` makes with
 * `hasBackendSettled`.
 */
export type AppConfigState = Readonly<{
    values: AppConfigValuesState;
    status: LoadStatus;
}>;

/** Default-entity factory for the persisted projection — no module configured yet. */
export function createDefaultAppConfigValuesState(): AppConfigValuesState {
    return { locationTracker: undefined };
}

export const initialAppConfigState: AppConfigState = {
    values: createDefaultAppConfigValuesState(),
    status: LoadStatus.Idle,
};
